import { useCallback, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';
import { useQueryParam } from 'use-query-params';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ExternalIntegrationVendor } from 'dashboard/models/external-integration-vendor';
import { TransactionTypeStatus } from 'dashboard/models/transaction-type';
import { Workflow } from 'dashboard/models/workflow';
import {
  WorkflowDeployment,
  WorkflowDeploymentConfigurationLabel,
} from 'dashboard/models/workflow-deployment';
import { WorkflowStepInput, WorkflowVersion } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setInputStep, setWorkflowVersion, undoWorkflow } from 'dashboard/redux/actions/workflow';
import { clearManualRunResults } from 'dashboard/redux/actions/workflow-runs';
import { clearWorkflowSchema } from 'dashboard/redux/actions/workflow-schema';
import { clearWorkflowVersions } from 'dashboard/redux/actions/workflow-versions';
import { useListExternalIntegrationVendorsQuery } from 'dashboard/redux/api/external-integration-vendor';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { RootState } from 'dashboard/redux/store';
import {
  archiveWorkflow,
  configureWorkflowEnvironments,
  discardDraftWorkflowVersion,
  loadPublishedWorkflowVersion,
  loadWorkflow,
  loadWorkflowVersion,
  pauseWorkflow,
  restoreWorkflowDraft,
  rollbackWorkflow,
  rolloutWorkflowVersion,
  runWorkflow,
  saveWorkflowVersion,
  updateWorkflow,
  validateWorkflow,
} from 'dashboard/redux/thunk-actions/workflow';
import { createSimulatedWorkflowRun } from 'dashboard/redux/thunk-actions/workflow-run';
import { createBulkManualRuns } from 'dashboard/redux/thunk-actions/workflow-runs';
import {
  fetchObjectJsonSchema,
  loadFilterFieldConfigs,
  loadWorkflowSchemaParallel,
} from 'dashboard/redux/thunk-actions/workflow-schema';
import { loadWorkflowVersionList } from 'dashboard/redux/thunk-actions/workflow-versions';
import { ConfigQueryParam } from 'dashboard/routes/DashboardWorkflowsShowRoute/types';
import DashboardWorkflowsShowView from 'dashboard/views/DashboardWorkflowsShowView';
import { EventType } from 'lib/constants/events';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useDebounced from 'lib/hooks/useDebounced';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { eventTypeFor } from 'lib/workflows';

const getVersionIdToRender = (
  workflow: Workflow,
  workflowDeployment: WorkflowDeployment | null,
): string | null => {
  const versionId =
    workflow?.relationships['latest-draft-version']?.data?.id ||
    workflow?.relationships['latest-published-version']?.data?.id;
  if (versionId) {
    return versionId;
  }
  if (workflowDeployment) {
    const { configuration } = workflowDeployment.attributes;
    const rolloutPercentage =
      configuration.versions.find(
        (version) => version.label === WorkflowDeploymentConfigurationLabel.Treatment,
      )?.percentage ?? 100;
    const treatmentVersionId = configuration.versions.find(
      (version) => version.label === WorkflowDeploymentConfigurationLabel.Treatment,
    )?.token;

    if (treatmentVersionId && rolloutPercentage < 100) {
      return treatmentVersionId;
    }
  }
  return null;
};

type Params = { workflowId: string };

const DashboardWorkflowsShowRoute = (props: PropsFromRedux) => {
  const {
    workflowSchema,
    currentEnvironment,
    workflow,
    workflowVersion,
    publishedWorkflowVersion,
    workflowDeployment,
    inputStep,
    canUndo,
    workflowVersionErrors,
    environments,
    manualRunResults,
    currentPage,
    pageSize,
    versionsList,
    setWorkflowVersion,
    setInputStep,
  } = props;

  const { trigger } = inputStep || {};

  useOnMountAndUnmount(props.clearWorkflowSchema);
  useOnMountAndUnmount(props.clearWorkflowVersions);

  const navigate = useNavigate();
  const { workflowId } = useParams<Params>() as Params; // workflowId is always defined
  const [workflowVersionIdQueryParam, setWorkflowVersionId] = useQueryParam<string | null>(
    'version',
  );
  const [configQueryParam] = useQueryParam<ConfigQueryParam>('config');
  const isTriggerCase = trigger?.type === ApiModel.TriggerCase;
  const editableVersionId = getVersionIdToRender(workflow, workflowDeployment);

  const [triggerIsDirty, setTriggerIsDirty] = useState(false);
  const [versionIsDirty, setVersionIsDirty] = useState(false);

  const workflowVersionsOfCurrentWorkflow = useMemo(
    () => versionsList.filter((version) => workflowId === version.relationships.workflow.data.id),
    [versionsList, workflowId],
  );

  const { data: externalIntegrationVendors = [] } = useListExternalIntegrationVendorsQuery();
  const [debouncedValidateWorkflow] = useDebounced(
    (vendors: ExternalIntegrationVendor[]) => props.validateWorkflow(vendors),
    333,
  );

  const routeToVersion = useCallback(
    (versionId: string) => setWorkflowVersionId(versionId, 'replace'),
    [setWorkflowVersionId],
  );
  const leavingRouteWithChanges = useCallback(
    () => triggerIsDirty || versionIsDirty,
    [triggerIsDirty, versionIsDirty],
  );

  // Load things
  const enablePlatformObjectJsonSchema = useFeatureFlag(
    'dashboard:workflow-use-platform-object-json-schema',
  );

  const { loading } = useApiLoader({
    loader: async () => {
      const [{ workflow, workflowDeployment }] = await Promise.all([
        props.loadWorkflow(workflowId),
        props.loadWorkflowSchemaParallel({
          loadCriteriaVariableSchemas: !enablePlatformObjectJsonSchema,
        }),
      ]);

      const versionId =
        workflowVersionIdQueryParam || getVersionIdToRender(workflow, workflowDeployment);
      if (!versionId) {
        throw new Error('Version ID is blank');
      }

      // maintain existing params
      const currentParams = new URLSearchParams(location.search);
      currentParams.set('version', versionId);

      navigate(
        {
          pathname: `/dashboard/workflows/${workflowId}`,
          search: currentParams.toString(),
        },
        { replace: true },
      );
      // loadWorkflowVersion needs to wait for all the schemas to load before running
      return props.loadWorkflowVersion(versionId);
    },
    onError: (error) => {
      if (location.pathname.includes('/case-actions')) {
        navigate('/dashboard/case-actions', { replace: true });
      } else {
        navigate('/dashboard/workflows', { replace: true });
      }

      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load workflow.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    // we intentionally leave out workflowVersionId here because we only want this to load once
    deps: [workflowId, currentEnvironment],
  });

  const {
    callback: loadWorkflowVersion,
    loading: isLoadingWorkflowVersion,
    error: loadWorkflowVersionError,
  } = useApiCallback(
    async (workflowVersionId: string) => {
      await props.loadWorkflowVersion(workflowVersionId);
      routeToVersion(workflowVersionId);

      setTriggerIsDirty(false);
      setVersionIsDirty(false);
    },
    [props.loadWorkflowVersion],
  );
  useApiErrorHandler({
    error: loadWorkflowVersionError,
    onError: (error) => {
      if (location.pathname.includes('/case-actions')) {
        navigate('/dashboard/case-actions', { replace: true });
      } else {
        navigate('/dashboard/workflows', { replace: true });
      }

      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load workflow.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingLatestPublishedVersion, callback: loadPublishedWorkflowVersion } =
    useApiCallback(async () => {
      await props.loadPublishedWorkflowVersion(workflow);
    }, [props.loadPublishedWorkflowVersion, workflow]);

  const { data: { data: transactionTypes = [] } = {}, error: transactionTypesError } =
    useListTransactionTypesQuery({ filter: { status: [TransactionTypeStatus.Active] } });

  useRTKErrorHandler({
    error: transactionTypesError,
    onError: () => ({ message: 'Failed to load Transaction Types.', level: MessageType.Failure }),
  });

  // callbacks
  const {
    callback: saveWorkflowVersion,
    loading: saveWorkflowVersionLoading,
    error: saveWorkflowVersionError,
  } = useApiCallback(async () => {
    // We set state before saving because saving will cause redirects triggering the unsaved changes prompt
    // FIXME: Need to figure out how to persist dirty state when saving fails
    if (triggerIsDirty || versionIsDirty) {
      setTriggerIsDirty(false);
      setVersionIsDirty(false);

      if (!trigger) {
        return;
      }

      const updatedVersionId = await props.saveWorkflowVersion(
        workflowId,
        workflowVersion,
        trigger,
      );
      if (updatedVersionId) {
        if (isTriggerCase) {
          navigate(`/dashboard/case-actions/${workflowId}/${updatedVersionId}`, { replace: true });
        } else {
          routeToVersion(updatedVersionId);
        }
        props.showMessage('Successfully saved workflow as a draft.', MessageType.Success);
      }
    }
  }, [
    workflowId,
    workflowVersionErrors,
    workflowVersion,
    trigger,
    triggerIsDirty,
    versionIsDirty,
    isTriggerCase,
    props.showMessage,
    props.saveWorkflowVersion,
    routeToVersion,
  ]);

  const { callback: updateWorkflow, error: saveWorkflowError } = useApiCallback(
    async (attributes: { name?: string; description?: string }) => {
      await props.updateWorkflow(workflow, attributes);
    },
    [workflow],
  );
  const handleUpdateWorkflow = (attributes: { name?: string; description?: string }) =>
    updateWorkflow(attributes);
  const {
    callback: configureEnvironments,
    loading: configureEnvironmentsLoading,
    error: configureEnvironmentsError,
  } = useApiCallback(
    async (activeEnvironmentIds: string[], rolloutPercentage: number) => {
      const previousWorkflowVersion = workflowDeployment?.attributes.configuration.versions.find(
        (config) => config.label === WorkflowDeploymentConfigurationLabel.Control,
      );
      await props.configureWorkflowEnvironments(
        workflow,
        activeEnvironmentIds,
        workflowVersion,
        rolloutPercentage,
        rolloutPercentage < 100 && previousWorkflowVersion
          ? previousWorkflowVersion.token
          : undefined,
      );
    },
    [workflow, workflowVersion, workflowDeployment],
  );
  const handleConfigureEnvironments = (activeEnvironmentIds: string[], rolloutPercentage: number) =>
    configureEnvironments(activeEnvironmentIds, rolloutPercentage);
  const {
    callback: rolloutWorkflowVersion,
    loading: rolloutWorkflowVersionLoading,
    error: rolloutWorkflowVersionError,
  } = useApiCallback(
    async (rolloutPercentage: number, description?: string, activeEnvironmentIds?: string[]) => {
      const environmentIds = activeEnvironmentIds ? activeEnvironmentIds : [currentEnvironment.id];
      if (rolloutPercentage === 0) {
        const result = await props.rollbackWorkflow(workflow, environmentIds);
        if (result) {
          navigate(result.redirectPath, { replace: true });
        }
      } else {
        const isDirty = versionIsDirty || triggerIsDirty;
        setTriggerIsDirty(false);
        setVersionIsDirty(false);
        if (!trigger) {
          return;
        }

        const nextPublishedVersionId = await props.rolloutWorkflowVersion(
          workflow,
          workflowVersion,
          trigger,
          rolloutPercentage,
          isDirty,
          environmentIds,
          { description },
        );

        if (nextPublishedVersionId) {
          if (isTriggerCase) {
            navigate(`/dashboard/case-actions/${workflowId}/${nextPublishedVersionId}`, {
              replace: true,
            });
          } else {
            routeToVersion(nextPublishedVersionId);
          }
          props.showMessage(
            `Successfully ${
              rolloutPercentage === 100 ? 'published' : 'rolled out'
            } workflow version.`,
            MessageType.Success,
          );
        }
      }
    },
    [
      workflow,
      workflowVersion,
      trigger,
      currentEnvironment,
      isTriggerCase,
      triggerIsDirty,
      versionIsDirty,
      props.rollbackWorkflow,
      props.rolloutWorkflowVersion,
      routeToVersion,
    ],
  );
  useApiErrorHandler({
    error: saveWorkflowError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 401) {
        props.showMessage('Failed to save workflow. Not authenticated.', MessageType.Failure);
      } else {
        props.showMessage('Failed to save workflow', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again',
        MessageType.Failure,
      ),
  });
  useApiErrorHandler({
    error: configureEnvironmentsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to configure workflow', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again',
        MessageType.Failure,
      ),
  });
  useApiErrorHandler({
    error: saveWorkflowVersionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save workflow, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to save workflow, please try again', MessageType.Failure),
  });

  useApiErrorHandler({
    error: rolloutWorkflowVersionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to rollout workflow, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to rollout workflow, please try again', MessageType.Failure),
  });

  const { callback: archiveWorkflow, error: archiveWorkflowError } = useApiCallback(async () => {
    await props.archiveWorkflow(workflow);

    navigate(`/dashboard/${isTriggerCase ? 'case-actions' : 'workflows'}`, { replace: true });
  }, [props.archiveWorkflow, workflow, isTriggerCase]);
  useApiErrorHandler({
    error: archiveWorkflowError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to archive workflow, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to archive workflow, please try again', MessageType.Failure),
  });

  const {
    callback: createBulkManualRuns,
    error: createBulkManualRunsError,
    loading: isLoadingCreateBulkManualRuns,
  } = useApiCallback(
    async (objectIds: string[]) => {
      await props.createBulkManualRuns(workflowId, objectIds);
    },
    [workflowId, props.createBulkManualRuns],
  );
  useApiErrorHandler({
    error: createBulkManualRunsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create manual runs, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to create manual runs, please try again', MessageType.Failure),
  });

  const {
    callback: loadVersionList,
    error: loadVersionListError,
    loading: isLoadingVersionList,
  } = useApiCallback(
    () =>
      props.loadWorkflowVersionList({
        workflowId,
        triggerType: trigger?.type,
        currentPage,
        pageSize,
      }),
    [props.loadWorkflowVersionList, currentPage, pageSize, workflowId],
  );
  useApiErrorHandler({
    error: loadVersionListError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load Version History.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: restoreDraftVersion } = useApiCallback(async () => {
    if (!workflowVersionIdQueryParam) {
      return;
    }

    const latestDraftVersionId = await props.restoreWorkflowDraft(
      workflowId,
      workflowVersionIdQueryParam,
    );
    const isTriggerCase = trigger?.type === ApiModel.TriggerCase;

    if (latestDraftVersionId) {
      const toLink = `/dashboard/${
        isTriggerCase ? 'case-actions' : 'workflows'
      }/${workflowId}/${latestDraftVersionId}`;
      navigate(toLink, { replace: true });
    }
    await loadVersionList();
    props.showMessage('Successfully restored draft version.', MessageType.Success);
  }, [workflowId, workflowVersionIdQueryParam, props.restoreWorkflowDraft, loadVersionList]);

  const { callback: handleDiscardDraft } = useApiCallback(async () => {
    if (!workflowVersionIdQueryParam) {
      return;
    }

    const result = await props.discardDraftWorkflowVersion(workflow, workflowVersionIdQueryParam);

    if (result) {
      navigate(result.redirectPath);
    }

    await loadVersionList();
  }, [workflowVersionIdQueryParam, workflow, props.discardDraftWorkflowVersion]);

  const setVersionAndValidate = useCallback(
    (workflowVersion: WorkflowVersion) => {
      setWorkflowVersion(workflowVersion);
      setVersionIsDirty(true);
      debouncedValidateWorkflow(externalIntegrationVendors);
    },
    [setWorkflowVersion, debouncedValidateWorkflow, externalIntegrationVendors],
  );
  const setInputStepAndValidate = useCallback(
    (inputStep: WorkflowStepInput) => {
      setInputStep(inputStep);
      setTriggerIsDirty(true);
      debouncedValidateWorkflow(externalIntegrationVendors);
    },
    [setInputStep, debouncedValidateWorkflow, externalIntegrationVendors],
  );

  if (
    loading ||
    workflow == null ||
    workflowVersion == null ||
    editableVersionId == null ||
    workflowSchema == null ||
    inputStep == null ||
    trigger == null ||
    (trigger.type === ApiModel.TriggerEvent &&
      eventTypeFor(trigger) === EventType.Transaction &&
      transactionTypes == null)
  ) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  }

  const handleClickSave = () => saveWorkflowVersion();
  const handleClickRollout = (
    rolloutPercentage: number,
    description?: string,
    activeEnvironmentIds?: string[],
  ) => rolloutWorkflowVersion(rolloutPercentage, description, activeEnvironmentIds);
  const handleClickArchive = (): Promise<void> => archiveWorkflow();

  return (
    <>
      <NavConfirmPrompt prevent={leavingRouteWithChanges} />
      <DashboardWorkflowsShowView
        loading={
          saveWorkflowVersionLoading ||
          rolloutWorkflowVersionLoading ||
          isLoadingLatestPublishedVersion ||
          isLoadingWorkflowVersion
        }
        configureEnvironmentsLoading={configureEnvironmentsLoading}
        workflowSchema={workflowSchema}
        transactionTypes={transactionTypes}
        configQueryParam={configQueryParam ?? null}
        onClickSave={handleClickSave}
        onConfigureEnvironments={handleConfigureEnvironments}
        onUpdateWorkflow={handleUpdateWorkflow}
        onClickRollout={handleClickRollout}
        onClickArchive={handleClickArchive}
        editingWorkflowVersionId={editableVersionId}
        workflow={workflow}
        workflowVersion={workflowVersion}
        publishedWorkflowVersion={publishedWorkflowVersion}
        workflowDeployment={workflowDeployment}
        environments={environments}
        organization={props.organization}
        isLoadingVersionList={isLoadingVersionList}
        versionsList={workflowVersionsOfCurrentWorkflow}
        loadWorkflowVersion={loadWorkflowVersion}
        loadVersionList={loadVersionList}
        loadLatestPublishedVersion={loadPublishedWorkflowVersion}
        restoreDraftVersion={restoreDraftVersion}
        handleDiscardDraft={handleDiscardDraft}
        pauseWorkflow={props.pauseWorkflow}
        createSimulatedWorkflowRun={props.createSimulatedWorkflowRun}
        runWorkflow={props.runWorkflow}
        setWorkflowVersion={setVersionAndValidate}
        setInputStep={setInputStepAndValidate}
        inputStep={inputStep}
        undoWorkflow={props.undoWorkflow}
        canUndo={canUndo}
        validationErrors={workflowVersionErrors}
        showMessage={props.showMessage}
        isDirty={triggerIsDirty || versionIsDirty}
        createBulkManualRuns={createBulkManualRuns}
        clearManualRunResults={props.clearManualRunResults}
        manualRunResults={manualRunResults}
        isLoadingCreateBulkManualRuns={isLoadingCreateBulkManualRuns}
        loadFilterFieldConfigs={props.loadFilterFieldConfigs}
        currentEnvironmentType={currentEnvironment.type}
        fetchPlatformObjectJsonSchema={props.fetchObjectJsonSchema}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  currentPage: state.workflowVersions.currentPage,
  pageSize: state.workflowVersions.pageSize,
  manualRunResults: state.workflowRuns.manualRunResults,
  organization: state.organization.organization,
  environments: state.organization.environments,
  workflow: state.workflow.present.workflow,
  workflowVersion: state.workflow.present.workflowVersion,
  publishedWorkflowVersion: state.workflow.present.publishedWorkflowVersion,
  workflowSchema: state.workflowSchema,
  workflowDeployment: state.workflow.present.workflowDeployment,
  inputStep: state.workflow.present.inputStep,
  canUndo: state.workflow.past.length > 0,
  workflowVersionErrors: state.workflow.present.workflowVersionErrors,
  versionsList: state.workflowVersions.workflowVersions,
});

const mapDispatchToProps = {
  loadWorkflowSchemaParallel,
  loadWorkflow,
  loadWorkflowVersion,
  loadWorkflowVersionList,
  loadPublishedWorkflowVersion,
  loadFilterFieldConfigs,
  archiveWorkflow,
  pauseWorkflow,
  createSimulatedWorkflowRun,
  runWorkflow,
  updateWorkflow,
  configureWorkflowEnvironments,
  saveWorkflowVersion,
  rolloutWorkflowVersion,
  rollbackWorkflow,
  setWorkflowVersion,
  showMessage,
  clearWorkflowVersions,
  undoWorkflow,
  clearWorkflowSchema,
  setInputStep,
  validateWorkflow,
  createBulkManualRuns,
  clearManualRunResults,
  restoreWorkflowDraft,
  discardDraftWorkflowVersion,
  fetchObjectJsonSchema,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWorkflowsShowRoute);
